import type { SearchResultV5 } from '@seek/chalice-types';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { memo, useEffect, useRef, useState } from 'react';
import hash from 'string-hash';

import type { TGooglePublisherTag } from './types';
import { loadScript } from './utils';

export type TargetingData = NonNullable<SearchResultV5['gptTargeting']>;

const DEFAULT_DEBOUNCE = 500;
const DEFAULT_RESIZE_DEBOUNCE = 300;

const useResizeAdListener = ({
  gpt,
  tagName,
  targetingData,
  responsiveMapping,
}: {
  gpt: TGooglePublisherTag | null;
  tagName: string;
  targetingData?: TargetingData;
  responsiveMapping: GooglePublisherTagResponsiveMapping[] | undefined;
}) => {
  const oldSize = useRef(window.innerWidth);
  const responsiveAdBreakpoints = responsiveMapping
    ?.filter((size) => size.minViewportWidth)
    .map((size) => size.minViewportWidth);

  useEffect(() => {
    const throttleRefreshAd = throttle(refreshAd, DEFAULT_DEBOUNCE);

    const updateSize = () => {
      if (gpt && responsiveAdBreakpoints) {
        const shouldUpdate = responsiveAdBreakpoints.find((breakpoint) =>
          didSizeChange({
            newSize: window.innerWidth,
            breakpoint,
            oldSize: oldSize.current,
          }),
        );

        if (shouldUpdate) {
          throttleRefreshAd({
            gpt,
            tagName,
            targetingData,
          });
        }
      }
      oldSize.current = window.innerWidth;
    };

    const updateSizeThrottle = throttle(updateSize, DEFAULT_RESIZE_DEBOUNCE);

    if (gpt) {
      window.addEventListener('resize', updateSizeThrottle);
      return () => {
        window.removeEventListener('resize', updateSizeThrottle);
        throttleRefreshAd.cancel();
        updateSizeThrottle.cancel();
      };
    }
  }, [gpt, responsiveAdBreakpoints, targetingData, tagName]);
};

export type SingleSizeArray = [number, number];
interface ManageAdProps {
  gpt: TGooglePublisherTag;
  tagName: string;
}

interface didSizeChangeProps {
  newSize: number;
  oldSize: number | undefined;
  breakpoint: number;
}

export const didSizeChange = ({
  newSize,
  oldSize,
  breakpoint,
}: didSizeChangeProps): boolean => {
  if (!oldSize) {
    return false;
  }

  return (
    (oldSize < breakpoint && newSize > breakpoint) ||
    (oldSize > breakpoint && newSize < breakpoint)
  );
};

const getSlotByTagName = ({ gpt, tagName }: ManageAdProps) =>
  gpt
    .pubads()
    .getSlots()
    .find((s) => s.getSlotElementId() === tagName);

interface DefineSlotProps {
  gpt: TGooglePublisherTag;
  tagId: string;
  sizes: GooglePublisherTagSize[];
  tagName: string;
}

const defineSlot = ({ gpt, tagId, sizes, tagName }: DefineSlotProps) => {
  const sizeMap: SingleSizeArray[] = sizes.map(({ height, width }) => [
    width,
    height,
  ]);
  return gpt.defineSlot(tagId, sizeMap, tagName)?.addService(gpt.pubads());
};

interface MappingBuilderProps {
  gpt: TGooglePublisherTag;
  responsiveMapping: GooglePublisherTagResponsiveMapping[];
}

const mappingBuilder = ({ gpt, responsiveMapping }: MappingBuilderProps) => {
  const mapping = gpt.sizeMapping();
  responsiveMapping.map(({ minViewportWidth, sizes: responsiveSizes }) => {
    const mappedResponsiveSizes: SingleSizeArray[] = responsiveSizes.map(
      ({ height, width }) => [width, height],
    );
    mapping.addSize([minViewportWidth, 0], mappedResponsiveSizes);
  });

  return mapping.build();
};

export const mappingBuilderForTest = mappingBuilder;

interface InitAdProps
  extends Omit<GooglePublisherTagProps, 'targetingData'>,
    ManageAdProps {}
export const initAd = ({
  gpt,
  tagName,
  tagId,
  sizes,
  responsiveMapping,
}: InitAdProps) => {
  gpt.cmd.push(() => {
    const slot = getSlotByTagName({
      gpt,
      tagName,
    });
    if (!slot) {
      const adSlot = defineSlot({ gpt, tagId, sizes, tagName });

      if (adSlot && responsiveMapping) {
        const mappingBuild = mappingBuilder({ gpt, responsiveMapping });
        if (mappingBuild) {
          adSlot.defineSizeMapping(mappingBuild);
        }
      }
      gpt.display(tagName);
    }
  });
};

interface RefreshAdProps
  extends Pick<GooglePublisherTagProps, 'targetingData'>,
    ManageAdProps {}
const refreshAd = ({ gpt, tagName, targetingData }: RefreshAdProps) => {
  gpt.cmd.push(() => {
    const slot = getSlotByTagName({
      gpt,
      tagName,
    });

    if (slot) {
      slot.clearTargeting();

      if (targetingData && Array.isArray(targetingData)) {
        targetingData.map(({ key, value }) => {
          slot.setTargeting(key, value);
        });
      }
      gpt.pubads().refresh([slot]);
    }
  });
};

interface GooglePublisherTagSize {
  width: number;
  height: number;
}

interface GooglePublisherTagResponsiveMapping {
  minViewportWidth: number;
  sizes: GooglePublisherTagSize[];
}

/**
 * @property {Array} sizes              - The actual sizes of the ad slot
 * @property {Array} responsiveMapping  - The mapping for the ad slots sizes servable by viewport dimensions
 */
interface GooglePublisherTagProps {
  tagId: string;
  sizes: GooglePublisherTagSize[];
  responsiveMapping?: GooglePublisherTagResponsiveMapping[];
  targetingData?: TargetingData;
}

const GooglePublisherTagView: React.FC<GooglePublisherTagProps> = (props) => {
  const { sizes, tagId, targetingData, responsiveMapping } = props;
  const [gpt, setGpt] = useState<TGooglePublisherTag | null>(null);
  const tagName = hash(tagId).toString();

  useResizeAdListener({ gpt, tagName, targetingData, responsiveMapping });

  useEffect(() => {
    const loadAndInit = async () => {
      const tag = await loadScript();
      setGpt(tag);
      initAd({
        gpt: tag,
        tagName,
        tagId,
        sizes,
        responsiveMapping,
      });
    };
    loadAndInit();
  }, [sizes, tagId, tagName, responsiveMapping]);

  useEffect(() => {
    const debounceRefreshAd = debounce(refreshAd, DEFAULT_DEBOUNCE);

    if (gpt) {
      debounceRefreshAd({
        gpt,
        tagName,
        targetingData,
      });
    }

    return () => {
      debounceRefreshAd.cancel();
    };
  }, [gpt, tagId, tagName, targetingData]);

  return (
    <div
      data-automation={`ad-${tagId}`}
      id={tagName}
      style={{ display: 'none' }}
    />
  );
};

export const GooglePublisherTag = memo(GooglePublisherTagView);
